import React from 'react';

/**
 * Button component with flexible styling and behavior options
 * 
 * @example
 * <Button variant="primary" size="lg" isLoading={isSubmitting}>
 *   Submit Form
 * </Button>
 * 
 * @example
 * <Button 
 *   variant="outline" 
 *   leftIcon={<PlusIcon />}
 *   onClick={handleAdd}
 *   className="custom-spacing"
 * >
 *   Add Item
 * </Button>
 */

export interface IButtonProps {
    // Content
    children: React.ReactNode;

    // Behavior
    onClick?: () => void;
    onSubmit?: () => void;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    isLoading?: boolean;

    // Styling variants
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'gradient' | 'custom';
    size?: 'sm' | 'md' | 'lg' | 'xl';

    // Layout and icons
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;

    // Customization
    className?: string;
    style?: React.CSSProperties;

    // Accessibility
    'aria-label'?: string;
    'aria-pressed'?: boolean;
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
}

export const Button: React.FC<IButtonProps> = ({
    children,
    onClick,
    onSubmit,
    type = 'button',
    disabled = false,
    isLoading = false,
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    fullWidth = false,
    className = '',
    style,
    ...ariaProps
}) => {
    // Base classes that apply to all buttons
    const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg';

    // Variant classes - leveraging existing CSS and extending
    const variantClasses = {
        primary: 'btn-primary', // Uses existing CSS class
        secondary: 'btn-secondary', // Uses existing CSS class  
        outline: 'btn-outline', // Uses existing CSS class
        ghost: 'bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900 focus:ring-gray-500',
        gradient: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white focus:ring-purple-500 shadow-lg hover:shadow-xl transform hover:scale-105',
        custom: '' // Allows completely custom styling via className
    };

    // Size classes
    const sizeClasses = {
        sm: 'px-3 py-2 text-sm min-h-[32px]',
        md: 'px-4 py-3 text-sm min-h-[44px]', // Default comfortable touch target
        lg: 'px-6 py-4 text-base min-h-[48px]',
        xl: 'px-8 py-4 text-lg min-h-[56px]'
    };

    // Loading state classes
    const loadingClasses = isLoading ? 'cursor-not-allowed opacity-75' : '';

    // Disabled state classes
    const disabledClasses = disabled && !isLoading ? 'cursor-not-allowed opacity-50' : '';

    // Full width classes
    const widthClasses = fullWidth ? 'w-full' : '';

    // Combine all classes
    const buttonClasses = [
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        loadingClasses,
        disabledClasses,
        widthClasses,
        className
    ].filter(Boolean).join(' ');

    // Handle click events
    const handleClick = () => {
        if (disabled || isLoading) return;

        if (type === 'submit' && onSubmit) {
            onSubmit();
        } else if (onClick) {
            onClick();
        }
    };

    // Loading spinner component
    const LoadingSpinner = () => (
        <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );

    return (
        <button
            type={type}
            onClick={handleClick}
            disabled={disabled || isLoading}
            className={buttonClasses}
            style={style}
            {...ariaProps}
        >
            {/* Left Icon */}
            {leftIcon && !isLoading && (
                <span className="mr-2 flex-shrink-0">
                    {leftIcon}
                </span>
            )}

            {/* Loading Spinner */}
            {isLoading && <LoadingSpinner />}

            {/* Button Content */}
            <span className={isLoading ? 'opacity-75' : ''}>
                {children}
            </span>

            {/* Right Icon */}
            {rightIcon && !isLoading && (
                <span className="ml-2 flex-shrink-0">
                    {rightIcon}
                </span>
            )}
        </button>
    );
};
